import Vue from 'vue'
import Router from 'vue-router'
import store from '../stores'

import authGuard from './authGuard'
import roleGuard from './roleGuard'

Vue.use(Router)

const index = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '*',
      redirect: '/'
    },

    /**
     * Administration Routes
     *  Routes for every role, except Customer.
     *
     */
    {
      path: '/admin',
      component: { render (c) { return c('router-view') }},
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'admin-home',
          // meta: {
          //   accessRoles: ['__REMOVE_WHEN_DASHBOARD_IS_COMPLETED__']
          // },
          component: () => import(/* webpackChunkName: "home" */ '../views/AdminHomePage.vue'),
        },
        {
          path: 'verify-email',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'verify-email',
              component: () => import(/* webpackChunkName: "verify-email" */ '../views/VerifyEmailPage.vue'),
            },
          ]
        },
        {
          path: 'finish-verify',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'finish-verify',
              component: () => import(/* webpackChunkName: "finish-verify" */ '../views/FinishVerifyEmailPage.vue'),
            },
          ]
        },
        {
          path: 'create',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: 'coupon',
              name: 'create-coupon',
              component: () => import(/* webpackChunkName: "create-coupon" */ '../views/Coupons/CouponCreatePage.vue'),
              meta: {
                breadcrumb: {
                  text: 'Coupons',
                  disabled: false,
                  routeName: '/coupons'
                }
              }
            },
            {
              path: ':productId/plan',
              name: 'create-plan',
              component: () => import(/* webpackChunkName: "create-plan" */ '../views/Plan/PlanCreatePage.vue'),
            },
            {
              path: 'service',
              name: 'create-service',
              component: () => import(/* webpackChunkName: "create-service" */ '../views/Services/ServiceCreatePage.vue'),
              meta: {
                breadcrumb: {
                  text: 'Services',
                  disabled: false,
                  routeName: '/services'
                }
              }
            },
            {
              path: 'customer',
              name: 'create-customer',
              component: () => import(/* webpackChunkName: "create-customer" */ '../views/Customer/CustomerCreatePage.vue'),
              meta: {
                breadcrumb: {
                  text: 'Customers',
                  disabled: false,
                  routeName: '/customers'
                }
              }
            },
            {
              path: 'subscription',
              name: 'create-subscription',
              component: () => import(/* webpackChunkName: "create-subscription" */ '../views/Subscriptions/SubscriptionCreatePage.vue'),
            },
            {
              path: 'product',
              name: 'create-product',
              component: () => import(/* webpackChunkName: "create-product" */ '../views/Product/ProductCreatePage.vue'),
            },
            {
              path: 'invoice',
              name: 'create-invoice',
              component: () => import(/* webpackChunkName: "create-invoice" */ '../views/Invoices/InvoiceCreatePage.vue'),
              async beforeEnter(to, from, next) {
                const { customer } = to.query || {}

                next()

                const invoice = customer
                  ? await store.dispatch('createInvoice', { customer })
                  : null

                invoice
                  ? next({ name: 'invoice-edit', params: { id: invoice.id }})
                  : next({ name: 'customer-detail', params: { id: customer }})

              }
            },
            {
              path: 'user',
              name: 'create-user',
              component: () => import(/* webpackChunkName: "create-user" */ '../views/User/UserCreatePage.vue'),
            },
          ]
        },
        {
          path: 'account',
          component: { render (c) { return c('router-view') }},
          meta: {
            accessRoles: ['owner']
          },
          children: [
            {
              path: '',
              name: 'account',
              component: () => import(/* webpackChunkName: "account-details" */ '../views/Account/AccountDetailPage.vue'),
            },
            {
              path: 'billing',
              name: 'account-billing',
              component: () => import(/* webpackChunkName: "account-billing" */ '../views/Account/AccountBillingPage.vue'),
            },
          ]
        },
        {
          path: 'invoices',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'invoices',
              component: () => import(/* webpackChunkName: "invoices" */ '../views/Invoices/InvoiceListPage.vue'),
            },
            {
              path: ':id/edit',
              name: 'invoice-edit',
              component: () => import(/* webpackChunkName: "invoice-edit" */ '../views/Invoices/InvoiceEditPage.vue'),
            },
            {
              path: ':id',
              name: 'invoice-detail',
              component: () => import(/* webpackChunkName: "invoice-detail" */ '../views/Invoices/InvoiceDetailPage.vue'),
            },
          ]
        },
        {
          path: 'services',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'services',
              component: () => import(/* webpackChunkName: "services" */ '../views/Services/ServiceListPage.vue'),
            },
            {
              path: ':id',
              name: 'service-detail',
              component: () => import(/* webpackChunkName: "service-detail" */ '../views/Services/ServiceDetailPage.vue'),
              meta: {
                breadcrumb: {
                  text: 'Services',
                  disabled: false,
                  routeName: '/services'
                }
              }
            },
          ]
        },
        {
          path: 'tax-rates',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'tax-rates',
              component: () => import(/* webpackChunkName: "tax-rates" */ '../views/TaxRate/TaxRateListPage.vue'),
            },
          ]
        },
        {
          path: 'coupons',
          component: { render (c) { return c('router-view') }},
          meta: {
            requiresAuth: true,
          },
          children: [
            {
              path: '',
              name: 'coupons',
              component: () => import(/* webpackChunkName: "coupons" */ '../views/Coupons/CouponListPage.vue'),
            },
            {
              path: ':id',
              name: 'coupon-detail',
              component: () => import(/* webpackChunkName: "coupon-detail" */ '../views/Coupons/CouponDetailPage.vue'),
              meta: {
                requiresAuth: true,
                breadcrumb: {
                  text: 'Coupons',
                  disabled: false,
                  routeName: '/coupons'
                }
              }
            },
          ]
        },
        {
          path: 'alerts',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'alerts',
              component: () => import(/* webpackChunkName: "alerts" */ '../views/Alert/AlertListPage.vue'),
            },
            {
              path: 'settings',
              name: 'alert-settings',
              component: () => import(/* webpackChunkName: "alert-settings" */ '../views/Alert/AlertSettingsPage.vue'),
            },
          ]
        },
        {
          path: 'crons',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'crons',
              component: () => import(/* webpackChunkName: "crons" */ '../views/Cron/CronListPage.vue'),
            },
          ]
        },
        {
          path: 'groups',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'groups',
              component: () => import(/* webpackChunkName: "groups" */ '../views/Group/GroupListPage.vue'),
            }
          ]
        },
        {
          path: 'divisions',
          component: { render(c) { return c('router-view')}},
          children: [
            {
              path: '',
              name: 'divisions',
              component: () => import(/* webpackChunkName: "divisions" */ '../views/Division/DivisionListPage.vue'),
            },
          ]
        },
        {
          path: 'events',
          component: { render(c) { return c('router-view')}},
          children: [
            {
              path: '',
              name: 'events',
              component: () => import(/* webpackChunkName: "events" */ '../views/Event/EventListPage.vue'),
            },
            {
              path: ':id',
              name: 'event-detail',
              component: () => import(/* webpackChunkName: "event-details" */ '../views/Event/EventDetailPage.vue'),
            },
          ]
        },
        {
          path: 'users',
          component: { render(c) { return c('router-view')}},
          children: [
            {
              path: '',
              name: 'users',
              component: () => import(/* webpackChunkName: "users" */ '../views/User/UserListPage.vue'),
            },
            {
              path: ':id',
              name: 'update-user',
              component: () => import(/* webpackChunkName: "" */ '../views/User/UserUpdatePage.vue'),
            },
          ]
        },
        {
          path: 'customers',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'customers',
              component: () => import(/* webpackChunkName: "customer-list" */ '../views/Customer/CustomerListPage.vue'),
            },
            {
              path: ':id',
              name: 'customer-detail',
              component: () => import(/* webpackChunkName: "customer-detail" */ '../views/Customer/CustomerDetailPage.vue'),
            }
          ]
        },
        {
          path: 'tasks',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'tasks',
              component: () => import(/* webpackChunkName: "tasks" */ '../views/Task/TasksPage.vue'),
            }
          ]
        },
        {
          path: 'profile',
          component: { render(c) { return c('router-view')}},
          children: [
            {
              path: '',
              name: 'profile',
              component: () => import(/* webpackChunkName: "profile" */ '../views/ProfilePage.vue'),

            }

          ]
        },
        {
          path: 'products',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'products',
              component: () => import(/* webpackChunkName: "products" */ '../views/Product/ProductListPage.vue'),
            },
            {
              path: ':id',
              name: 'product-detail',
              component: () => import(/* webpackChunkName: "product-detail" */ '../views/Product/ProductDetailPage.vue'),
              meta: {
                breadcrumb: {
                  text: 'products',
                  disabled: false,
                  routeName: '/products'
                }
              }
            },
          ]
        },
        {
          path: 'schedule',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'schedule',
              component: () => import(/* webpackChunkName: "schedule" */ '../views/Schedule/SchedulePage.vue'),
            }
          ]
        },
        {
          path: 'bookings',
          component: { render (c) { return c('router-view') }},
          children: [
            {
              path: '',
              name: 'bookings',
              component: () => import(/* webpackChunkName: "bookings" */ '../views/Bookings/BookingsPage.vue'),
            }
          ]
        }
      ]
    },

    /**
     * Platform Routes
     * These are routes requires for operating Hiveway
     */
    {
      path: '/platform',
      component: {
        render(c) {
          return c('router-view')
        }
      },
      meta: {
        isPlatform: true,
        hideHeader: true
      },
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import(/* webpackChunkName: "login" */ '../views/LoginPage.vue'),
          meta: {
            hideHeader: true
          }
        },
        {
          path: 'request-invite',
          name: 'request-invite',
          component: () => import(/* webpackChunkName: "signup" */ '../views/RequestInvitePage.vue'),
        },
        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import(/* webpackChunkName: "reset-password" */ '../views/ResetPasswordPage.vue'),
          meta: {
            hideHeader: true
          }
        },
        {
          path: 'token/:id',
          name: 'platform-token',
          component: () => import(/* webpackChunkName: "platform-token" */ '../views/PlatformTokenPage.vue'),
          meta: {
            preventRedirect: true,
            hideHeader: true
          }
        },
        {
          path: 'signup',
          name: 'signup',
          component: () => import(/* webpackChunkName: "signup" */ '../views/SignUpPage.vue'),
          meta: {
            hideHeader: true
          }
        }
      ]
    },

    /**
     * Public Tenant Routes
     * Routes for customer facing pages
     *
     * NOTE: These routes are NOT for Hiveway's Customers
     */
    {
      path: '/',
      component: { render (c) { return c('router-view') }},
      meta: {
        isPublic: true,
      },
      children: [
        {
          path: '/',
          redirect: '/platform/login'
        },
        {
          path: 'catalogue',
          name: 'public-catalogue',
          component: () => import(/* webpackChunkName: "public-catalogue" */ '../views/Public/PublicCataloguePage.vue'),
        },
      ]
    },
  ]
});

index.beforeEach(authGuard);
index.beforeEach(roleGuard);

export default index;
